import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { SAMPLE_USERS } from '../lib/userSystem';
import { fmtDate } from '../lib/utils';
import { todayKey } from '../lib/dateHelpers';
import { DETAIL_TASK_TYPE, getEventDetailStatus } from '../lib/eventCompleteness';
import {
  Plus, Check, Trash2, Calendar, User, Clock,
  CheckCircle2, Circle, AlertTriangle, Filter,
} from 'lucide-react';

/**
 * EventTasks — reusable task list component
 *
 * Props:
 * - eventId: string|null — if provided, shows tasks for this event; if null, standalone mode
 * - compact: boolean — if true, shows a condensed view (for Dashboard sidebar)
 * - showEventName: boolean — if true, shows which event each task belongs to
 * - filterUserId: string — if provided, only shows tasks assigned to this user
 * - onTaskClick: (task) => void — optional callback when a task is clicked
 */
export default function EventTasks({
  eventId = null,
  compact = false,
  showEventName = false,
  filterUserId = null,
  onTaskClick,
}) {
  const {
    eventTasks, addEventTask, toggleEventTask, updateEventTask, deleteEventTask,
    events, currentUser, availableUsers,
  } = useAppContext();

  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [filterView, setFilterView] = useState('all'); // 'all', 'mine', 'overdue'
  const [showAddForm, setShowAddForm] = useState(false);

  const inputRef = useRef(null);
  const mentionRef = useRef(null);

  // Filter tasks for this context
  const tasks = useMemo(() => {
    let filtered = eventTasks;

    if (eventId) {
      // Inside an event — show only this event's tasks
      filtered = filtered.filter((t) => t.eventId === eventId);
    } else if (filterUserId) {
      // Dashboard — show tasks assigned to this user (both event and standalone)
      filtered = filtered.filter((t) => t.assignedTo === filterUserId);
    }

    // Apply view filter
    if (filterView === 'mine') {
      filtered = filtered.filter((t) => t.assignedTo === currentUser?.id);
    } else if (filterView === 'overdue') {
      const today = todayKey();
      filtered = filtered.filter((t) => !t.completed && t.dueDate && t.dueDate < today);
    }

    return filtered;
  }, [eventTasks, eventId, filterUserId, filterView, currentUser?.id]);

  const activeTasks = tasks.filter((t) => !t.completed);
  const completedTasks = tasks.filter((t) => t.completed);

  // Sort: overdue first, then by due date, then by created date
  const sortedActive = useMemo(() => {
    const today = todayKey();
    return [...activeTasks].sort((a, b) => {
      // Overdue items first
      const aOverdue = a.dueDate && a.dueDate < today ? 0 : 1;
      const bOverdue = b.dueDate && b.dueDate < today ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      // Then by due date (earliest first, no date last)
      if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
      if (a.dueDate) return -1;
      if (b.dueDate) return 1;
      // Then by created date (newest first)
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
  }, [activeTasks]);

  // @mention autocomplete
  const users = availableUsers || SAMPLE_USERS;
  const filteredUsers = users.filter((u) =>
    u.name.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  // Handle typing in the input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewTaskText(value);

    // Check for @ trigger
    const lastAt = value.lastIndexOf('@');
    if (lastAt >= 0) {
      const afterAt = value.substring(lastAt + 1);
      // Only show if there's no space after the @ (still typing the name)
      if (!afterAt.includes(' ') || afterAt.length === 0) {
        setMentionFilter(afterAt);
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  // Select a user from the mention dropdown
  const handleSelectMention = (user) => {
    const lastAt = newTaskText.lastIndexOf('@');
    const before = newTaskText.substring(0, lastAt);
    const newText = `${before}@${user.name} `;
    setNewTaskText(newText);
    setSelectedAssignee(user);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  // Handle keyboard in mention dropdown
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowMentions(false);
    }
    if (e.key === 'Enter' && !showMentions) {
      e.preventDefault();
      handleAddTask();
    }
  };

  // Add the task
  const handleAddTask = async () => {
    const text = newTaskText.trim();
    if (!text) return;

    // Strip @mentions from the title for clean display
    const cleanTitle = text.replace(/@\w+/g, '').trim();

    try {
      await addEventTask({
        eventId: eventId || null,
        title: cleanTitle || text,
        assignedTo: selectedAssignee?.id || null,
        assignedToName: selectedAssignee?.name || '',
        dueDate: newTaskDueDate || null,
        priority: 'normal',
      });
    } catch (err) {
      alert(`Failed to add task: ${err.message}`);
      return;
    }

    setNewTaskText('');
    setNewTaskDueDate('');
    setSelectedAssignee(null);
    setShowAddForm(false);
  };

  // Close mentions when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (mentionRef.current && !mentionRef.current.contains(e.target)) {
        setShowMentions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const today = todayKey();

  // Get event name lookup for showing event context
  const eventNameMap = useMemo(() => {
    if (!showEventName) return {};
    const map = {};
    events.forEach((e) => { map[e.id] = e.name; });
    return map;
  }, [events, showEventName]);

  const TaskItem = ({ task }) => {
    const isOverdue = !task.completed && task.dueDate && task.dueDate < today;
    const isDueToday = !task.completed && task.dueDate === today;
    const evtName = showEventName && task.eventId ? eventNameMap[task.eventId] : null;
    // System task: closes itself when the event details are complete, so it
    // can't be ticked off or deleted by hand while details are still missing.
    const isAutoDetail = task.autoType === DETAIL_TASK_TYPE && !task.completed;
    const autoMissing = isAutoDetail
      ? getEventDetailStatus(events.find((e) => e.id === task.eventId) || {}).missing.map((m) => m.label)
      : [];

    return (
      <div
        className={`flex items-start gap-2 p-2 rounded-lg border transition group ${
          task.completed
            ? 'bg-gray-50 border-gray-100 opacity-60'
            : isOverdue
            ? 'bg-red-50 border-red-200'
            : isDueToday
            ? 'bg-amber-50 border-amber-200'
            : 'bg-white border-gray-100 hover:border-gray-200'
        }`}
      >
        {/* Checkbox */}
        <button
          onClick={() => { if (!isAutoDetail) toggleEventTask(task.id); }}
          className={`mt-0.5 flex-shrink-0 ${isAutoDetail ? 'cursor-not-allowed' : ''}`}
          title={isAutoDetail ? 'Clears itself once every event detail is filled in' : task.completed ? 'Mark incomplete' : 'Mark complete'}
        >
          {task.completed ? (
            <CheckCircle2 size={18} className="text-emerald-500" />
          ) : (
            <Circle size={18} className={isOverdue ? 'text-red-400' : 'text-gray-300 hover:text-emerald-400'} />
          )}
        </button>

        {/* Content */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => onTaskClick?.(task)}
        >
          <p className={`text-sm ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
            {task.title}
          </p>
          {isAutoDetail && autoMissing.length > 0 && (
            <p className="text-xs text-red-700 mt-0.5">Missing: {autoMissing.join(', ')}</p>
          )}
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {evtName && (
              <span className="text-xs text-purple-600 font-medium truncate max-w-[150px]">{evtName}</span>
            )}
            {!evtName && !task.eventId && showEventName && (
              <span className="text-xs text-gray-400 italic">General</span>
            )}
            {task.assignedToName && (
              <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                <User size={10} /> {task.assignedToName}
              </span>
            )}
            {task.dueDate && (
              <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded ${
                isOverdue ? 'text-red-700 bg-red-100 font-medium' : isDueToday ? 'text-amber-700 bg-amber-100 font-medium' : 'text-gray-500 bg-gray-100'
              }`}>
                <Clock size={10} /> {fmtDate(task.dueDate)}
                {isOverdue && ' (overdue)'}
              </span>
            )}
            {task.createdByName && !compact && (
              <span className="text-xs text-gray-400">by {task.createdByName}</span>
            )}
          </div>
        </div>

        {/* Delete */}
        {!isAutoDetail && <button
          onClick={() => {
            if (window.confirm(`Delete task "${task.title}"?`)) {
              deleteEventTask(task.id);
            }
          }}
          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition flex-shrink-0 mt-0.5"
          title="Delete task"
        >
          <Trash2 size={14} />
        </button>}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Header with filters (not in compact mode) */}
      {!compact && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {['all', 'mine', 'overdue'].map((view) => (
              <button
                key={view}
                onClick={() => setFilterView(view)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                  filterView === view
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {view === 'all' ? 'All' : view === 'mine' ? 'Mine' : 'Overdue'}
                {view === 'overdue' && tasks.filter((t) => !t.completed && t.dueDate && t.dueDate < today).length > 0 && (
                  <span className="ml-1 px-1 rounded-full bg-red-500 text-white text-[10px]">
                    {tasks.filter((t) => !t.completed && t.dueDate && t.dueDate < today).length}
                  </span>
                )}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400">
            {activeTasks.length} open{completedTasks.length > 0 ? ` · ${completedTasks.length} done` : ''}
          </span>
        </div>
      )}

      {/* Add Task */}
      {!showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center gap-2 p-2 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 hover:border-emerald-300 hover:text-emerald-600 transition text-sm"
        >
          <Plus size={16} /> Add task{selectedAssignee ? '' : '...'}
        </button>
      ) : (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 space-y-2">
          {/* Task input with @mention */}
          <div className="relative" ref={mentionRef}>
            <input
              ref={inputRef}
              type="text"
              value={newTaskText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Task description... use @ to assign"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />

            {/* @mention dropdown */}
            {showMentions && filteredUsers.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectMention(user)}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm flex items-center gap-2"
                  >
                    <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {user.initials}
                    </span>
                    <span className="font-medium">{user.name}</span>
                    <span className="text-xs text-gray-400 ml-auto">{user.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Due date and assignee display */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Calendar size={14} className="text-gray-400" />
              <input
                type="date"
                value={newTaskDueDate}
                onChange={(e) => setNewTaskDueDate(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                placeholder="Due date"
              />
            </div>

            {selectedAssignee && (
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                <User size={10} /> {selectedAssignee.name}
                <button
                  onClick={() => {
                    setSelectedAssignee(null);
                    // Remove @mention from text
                    setNewTaskText((prev) => prev.replace(/@\w+\s?/, ''));
                  }}
                  className="ml-1 text-blue-400 hover:text-blue-600"
                >×</button>
              </span>
            )}

            <div className="ml-auto flex gap-1">
              <button
                onClick={() => { setShowAddForm(false); setNewTaskText(''); setNewTaskDueDate(''); setSelectedAssignee(null); }}
                className="px-3 py-1 text-xs text-gray-500 hover:bg-gray-200 rounded transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTask}
                disabled={!newTaskText.trim()}
                className="px-3 py-1 text-xs text-white bg-emerald-600 hover:bg-emerald-700 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Tasks */}
      {sortedActive.length > 0 ? (
        <div className="space-y-1.5">
          {sortedActive.map((task) => (
            <TaskItem key={task.id} task={task} />
          ))}
        </div>
      ) : (
        !compact && <p className="text-sm text-gray-400 text-center py-3">No open tasks</p>
      )}

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <div>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="text-xs text-gray-400 hover:text-gray-600 transition flex items-center gap-1"
          >
            <Check size={12} />
            {showCompleted ? 'Hide' : 'Show'} {completedTasks.length} completed
          </button>
          {showCompleted && (
            <div className="space-y-1 mt-1.5">
              {completedTasks.map((task) => (
                <TaskItem key={task.id} task={task} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
